package com.fireavert.react.shared.styles

import com.fireavert.react.shared.styles.MobileStyles.MOBILE_VIEW_WIDTH
import emotion.css.ClassName
import react.dom.html.ReactHTML.span
import web.cssom.*

object ModernNavStyles {
    // Constants
    private val SIDEBAR_WIDTH = 260.px
    private val SIDEBAR_COLLAPSED_WIDTH = 70.px
    private val TRANSITION_SPEED = 0.25.s
    private val PRIMARY_COLOR = Color("#262626") // Keep original black
    private val ACCENT_COLOR = Color("#C73035") // Keeping the original accent color
    private val TEXT_COLOR = Color("#FFFFFF")
    private val SECONDARY_TEXT_COLOR = Color("#94A3B8")
    private val HOVER_BG_COLOR = Color("#353535")

    // Main sidebar container
    val sideNav = ClassName {
        width = SIDEBAR_WIDTH
        minWidth = SIDEBAR_WIDTH
        height = 100.vh
        backgroundColor = PRIMARY_COLOR
        display = Display.flex
        flexDirection = FlexDirection.column
        transition = Transition(TransitionProperty.all, TRANSITION_SPEED, TransitionTimingFunction.easeInOut)
//        boxShadow = BoxShadow(0.px, 0.px, 15.px, rgb(0, 0, 0, 0.1))
        overflow = Overflow.hidden
        zIndex = integer(1000)

        media(MediaQuery(MOBILE_VIEW_WIDTH)) {
            position = Position.fixed
            transform = translatex((-100).pct)
            left = 0.px
            top = 0.px
        }
    }

    // Collapsed state
    val sideNavCollapsed = ClassName {
        width = SIDEBAR_COLLAPSED_WIDTH
        minWidth = SIDEBAR_COLLAPSED_WIDTH
    }

    // Header section with logo and collapse button
    val sideNavHeader = ClassName {
        display = Display.flex
        justifyContent = JustifyContent.spaceBetween
        alignItems = AlignItems.center
        padding = Padding(16.px, 16.px)
        height = 50.px
        borderBottom = Border(1.px, LineStyle.solid, rgb(255, 255, 255, 0.1))
        background = NamedColor.white
    }

    val logo = ClassName {
        height = 40.px
        maxWidth = 160.px
        objectFit = ObjectFit.contain
    }

    val collapseButton = ClassName {
        display = Display.flex
        justifyContent = JustifyContent.center
        alignItems = AlignItems.center
        width = 28.px
        height = 28.px
        borderRadius = 4.px
        backgroundColor = rgb(255, 255, 255, 0.1)
        border = None.none
        color = TEXT_COLOR
        cursor = Cursor.pointer

        hover {
            backgroundColor = rgb(255, 255, 255, 0.2)
        }
    }

    val collapseIcon = ClassName {
        fontSize = 14.px
    }

    // Filters section
    val filtersSection = ClassName {
        padding = important(Padding(16.px, 16.px))
        borderBottom = Border(1.px, LineStyle.solid, rgb(255, 255, 255, 0.1))
    }

    val filterLabel = ClassName {
        fontSize = 12.px
        fontWeight = FontWeight.bold
        color = rgb(148, 163, 184)
        marginBottom = 12.px
        letterSpacing = 1.px
        padding = Padding(6.px, 0.px, 8.px)
        display = Display.flex
        alignItems = AlignItems.center
        gap = 8.px
    }

    val divider = ClassName {
        marginTop = 16.px
        marginBottom = 16.px
    }

    val muiDropdownFix = ClassName {
        // Fix for Autocomplete component
        borderRadius = 20.px
        "& .MuiAutocomplete-root" {
            backgroundColor = important(NamedColor.white)
            marginBottom = 16.px
            padding = important(Padding(4.px, 4.px))
        }

        "& .MuiInputBase-root" {
            backgroundColor = important(NamedColor.white)
            color = important(NamedColor.black)
            borderRadius = 25.px
            padding = important(Padding(4.px, 4.px))
        }

        "& .MuiOutlinedInput-notchedOutline" {
            borderColor = important(Color("#c4c4c4"))
        }

        "& .MuiInputBase-input" {
            backgroundColor = important(NamedColor.white)
            color = important(NamedColor.black)
            padding = important(Padding(4.px, 4.px))
        }

        "& .MuiOutlinedInput-root" {
            backgroundColor = important(NamedColor.white)
            color = important(NamedColor.black)
            borderRadius = important(25.px)
            padding = important(Padding(4.px, 4.px))
        }

        "& .MuiFormControl-root" {
            backgroundColor = important(NamedColor.white)
            marginBottom = 16.px
            borderRadius = 4.px
            overflow = Overflow.hidden
        }

        // Fix for Select component
        "& .MuiSelect-select" {
            backgroundColor = important(NamedColor.white)
            padding = important(Padding(10.5.px, 8.px))
        }

        // Fix for dropdown menus
        "& .MuiPopover-paper, & .MuiPaper-root, & .MuiMenu-paper, & .MuiMenu-list" {
            backgroundColor = important(NamedColor.white)
        }

        // Fix for icons
        "& .MuiSvgIcon-root" {
            color = important(NamedColor.black)
        }

        // Fix padding
        "& .MuiInputLabel-root" {
            backgroundColor = important(NamedColor.white)
            paddingLeft = 5.px
            paddingRight = 5.px
            marginLeft = (-5).px
            fontSize = 14.px
        }
    }

    // Specific styles for dropdowns
    val dropdownOverrides = ClassName {
        // These styles ensure the dropdowns have proper background and spacing
        "& .MuiInputBase-root" {
            backgroundColor = important(NamedColor.white)
        }
        "& .MuiOutlinedInput-notchedOutline" {
            borderColor = Color("#c4c4c4")
        }
        "& .MuiFormControl-root + .MuiFormControl-root" {
            marginTop = 16.px // Add space between consecutive dropdowns
        }
        "& .MuiAutocomplete-root" {
            marginBottom = 16.px // Add space after autocomplete
        }
    }

    // Navigation sections
    val navSection = ClassName {
        flex = number(1.0)
        overflowY = Auto.auto
        display = Display.flex
        flexDirection = FlexDirection.column
        gap = 24.px
        padding = Padding(16.px, 0.px)
    }

    val navGroup = ClassName {
        display = Display.flex
        flexDirection = FlexDirection.column
        gap = 4.px
    }

    val navGroupLabel = ClassName {
        fontSize = 12.px
        fontWeight = FontWeight.bold
        color = SECONDARY_TEXT_COLOR
        padding = Padding(0.px, 16.px, 8.px, 16.px)
        letterSpacing = 1.px
    }

    // Navigation items
    val navItem = ClassName {
        display = Display.flex
        alignItems = AlignItems.center
        padding = Padding(12.px, 16.px)
        borderRadius = 6.px
        marginLeft = 8.px
        marginRight = 8.px
        cursor = Cursor.pointer
        color = TEXT_COLOR
        position = Position.relative
        transition = Transition(TransitionProperty.all, 0.15.s, TransitionTimingFunction.easeInOut)

        hover {
            backgroundColor = HOVER_BG_COLOR
        }
    }

    val navItemActive = ClassName {
        backgroundColor = ACCENT_COLOR
        color = NamedColor.white
        fontWeight = FontWeight.bold

        hover {
            backgroundColor = ACCENT_COLOR
        }
    }

    val navItemIcon = ClassName {
        width = 20.px
        height = 20.px
        opacity = number(0.9)
    }

    val navItemText = ClassName {
        marginLeft = 12.px
        fontSize = 14.px
        whiteSpace = WhiteSpace.nowrap
    }

    val navItemBadge = ClassName {
        display = Display.flex
        justifyContent = JustifyContent.center
        alignItems = AlignItems.center
        minWidth = 20.px
        height = 20.px
        borderRadius = 10.px
        backgroundColor = rgb(255, 255, 255, 0.2)
        fontSize = 12.px
        marginLeft = Auto.auto
        padding = Padding(0.px, 6.px)
    }

    val navItemBadgeActive = ClassName {
        backgroundColor = rgb(255, 255, 255, 0.3)
    }

    val navItemMiniBadge = ClassName {
        position = Position.absolute
        top = 8.px
        right = 8.px
        fontSize = 20.px
        color = ACCENT_COLOR
    }

    // Footer section
    val footerSection = ClassName {
        padding = Padding(16.px, 16.px)
        borderTop = Border(1.px, LineStyle.solid, rgb(255, 255, 255, 0.1))
        display = Display.flex
        justifyContent = JustifyContent.center
    }

    val privacyPolicyLink = ClassName {
        color = SECONDARY_TEXT_COLOR
        fontSize = 12.px
        textDecoration = None.none

        hover {
            color = TEXT_COLOR
            textDecoration = TextDecoration.underline
        }
    }

    // Mobile navigation overlay (backdrop)
    val mobileNavOverlay = ClassName {
        position = Position.fixed
        top = 0.px
        left = 0.px
        width = 100.vw
        height = 100.vh
        backgroundColor = rgb(0, 0, 0, 0.5)
        zIndex = integer(1200)
        visibility = Visibility.hidden
        opacity = number(0.0)
        transition = Transition(TransitionProperty.all, 0.3.s, TransitionTimingFunction.easeInOut)
    }

    val mobileNavOverlayOpen = ClassName {
        visibility = Visibility.visible
        opacity = number(1.0)
    }

    // Mobile navigation drawer
    val mobileNavDrawer = ClassName {
        position = Position.fixed
        top = 0.px
        left = 0.px
        width = 280.px
        height = 100.vh
        backgroundColor = PRIMARY_COLOR
        zIndex = integer(1300)
        transform = translatex((-100).pct)
        transition = Transition(TransitionProperty.all, 0.3.s, TransitionTimingFunction.easeInOut)
        display = Display.flex
        flexDirection = FlexDirection.column
        boxShadow = BoxShadow(0.px, 0.px, 20.px, rgb(0, 0, 0, 0.3))
    }

    val mobileNavDrawerOpen = ClassName {
        transform = translatex(0.px)
    }

    // Mobile navigation header
    val mobileNavHeader = ClassName {
        display = Display.flex
        justifyContent = JustifyContent.spaceBetween
        alignItems = AlignItems.center
        padding = Padding(16.px, 16.px)
        height = 70.px
        borderBottom = Border(1.px, LineStyle.solid, rgb(255, 255, 255, 0.1))
        background = NamedColor.white
    }

    val mobileNavLogo = ClassName {
        height = 36.px
        maxWidth = 140.px
        objectFit = ObjectFit.contain
    }

    val mobileNavCloseButton = ClassName {
        width = 40.px
        height = 40.px
        display = Display.flex
        justifyContent = JustifyContent.center
        alignItems = AlignItems.center
        fontSize = 28.px
        color = TEXT_COLOR
        cursor = Cursor.pointer
        borderRadius = 50.pct

        hover {
            backgroundColor = rgb(255, 255, 255, 0.1)
        }
    }

    // Navigation items container
    val mobileNavItems = ClassName {
        flex = number(1.0)
        overflowY = Auto.auto
        padding = Padding(16.px, 0.px)
        display = Display.flex
        flexDirection = FlexDirection.column
        gap = 24.px
    }

    val mobileNavGroup = ClassName {
        display = Display.flex
        flexDirection = FlexDirection.column
        gap = 4.px
    }

    val mobileNavGroupLabel = ClassName {
        fontSize = 12.px
        fontWeight = FontWeight.bold
        color = SECONDARY_TEXT_COLOR
        padding = Padding(0.px, 16.px, 8.px, 16.px)
        letterSpacing = 1.px
    }

    // Individual nav items
    val mobileNavItem = ClassName {
        display = Display.flex
        alignItems = AlignItems.center
        padding = Padding(14.px, 16.px)
        cursor = Cursor.pointer
        color = TEXT_COLOR
        borderRadius = 6.px
        marginLeft = 8.px
        marginRight = 8.px
        transition = Transition(TransitionProperty.all, 0.15.s, TransitionTimingFunction.easeInOut)

        hover {
            backgroundColor = HOVER_BG_COLOR
        }
    }

    val mobileNavItemActive = ClassName {
        backgroundColor = ACCENT_COLOR
        color = NamedColor.white
        fontWeight = FontWeight.bold

        hover {
            backgroundColor = ACCENT_COLOR
        }
    }

    val mobileNavItemIcon = ClassName {
        width = 20.px
        height = 20.px
        opacity = number(0.9)
    }

    // Mobile navigation footer
    val mobileNavFooter = ClassName {
        padding = Padding(16.px, 16.px)
        borderTop = Border(1.px, LineStyle.solid, rgb(255, 255, 255, 0.1))
        textAlign = TextAlign.center
        color = SECONDARY_TEXT_COLOR
        fontSize = 14.px
    }

    // Mobile top bar (visible at the top of the screen on mobile)
    val mobileTopBar = ClassName {
        position = Position.fixed
        top = 0.px
        left = 0.px
        width = 100.pct
        height = 60.px
        backgroundColor = Color("#1A1A1A") // Slightly lighter than sidebar
        display = Display.flex
        alignItems = AlignItems.center
        justifyContent = JustifyContent.spaceBetween
        padding = Padding(0.px, 16.px)
        boxShadow = BoxShadow(0.px, 2.px, 8.px, rgb(0, 0, 0, 0.2))
        zIndex = integer(1000)
    }

    val mobileMenuButton = ClassName {
        width = 40.px
        height = 40.px
        display = Display.flex
        justifyContent = JustifyContent.center
        alignItems = AlignItems.center
        cursor = Cursor.pointer
        borderRadius = 4.px

        hover {
            backgroundColor = rgb(255, 255, 255, 0.1)
        }
    }

    // Hamburger icon with three lines
    val hamburgerIcon = ClassName {
        width = 24.px
        height = 18.px
        position = Position.relative
        display = Display.flex
        flexDirection = FlexDirection.column
        justifyContent = JustifyContent.spaceBetween

        span {
            display = Display.block
            height = 2.px
            width = 100.pct
            backgroundColor = NamedColor.white
            borderRadius = 2.px
        }
    }

    val mobileNavItemText = ClassName {
        marginLeft = 12.px
        fontSize = 16.px // Mobile text can be slightly larger for readability
    }

    // ADD THIS NEW STYLE:
    val mobilePrivacyPolicyLink = ClassName {
        color = SECONDARY_TEXT_COLOR // Uses the private val from this object
        fontSize = 12.px
        textDecoration = None.none
        display = Display.inlineBlock // Ensures it behaves well within the centered footer
        padding = Padding(8.px, 0.px) // Give it some touch area / spacing

        hover {
            color = TEXT_COLOR
            textDecoration = TextDecoration.underline
        }
    }
}